import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FILTER_TYPES, IFilterData } from 'src/app/core/feature-modules/table-filter/table-filter.interfaces';
import { LazyLoadEvent } from 'primeng/api';
import { DBService } from '../../core/backend-adapter/db.service';
import { SocketService } from '../../core/backend-adapter/socket.service';
import { NotifyService } from 'src/app/core/layouts/notifications/notify/notify.service';
import { TableFilterService } from 'src/app/core/feature-modules/table-filter/table-filter.service';
import { DialogService } from 'primeng/dynamicdialog';
import { Table } from 'primeng/table';
import { Subject, combineLatest, Observable, forkJoin } from 'rxjs';
import { takeUntil, filter as rxFilter, take, debounceTime, distinctUntilChanged, finalize } from 'rxjs/operators';
import { AddEditResponseComponent } from '../components/add-edit-response/add-edit-response.component';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  ICols,
  IFitersObject,
  IPublish,
  IReviewData,
  IReviewMulti,
  IReviewsMetrics,
  IReviewsMetricsResponse,
  PeriodText,
  PeriodType,
} from '../review-management.interfaces';
import { DeletePopupComponent } from '../components/delete-popup/delete-popup.component';
import { SYSTEM_COLORS } from 'src/app/core/feature-modules/whitelabel/style-changer/styles/colors/system-colors.constants';
import { SessionService } from 'src/app/core/backend-adapter/session.service';
import { ExportDataToFile } from '../services/export-reviews-file.service';
import { IAccount } from 'src/app/acct-comps/accounts.interfaces';
import { ReviewsCopyClipboardService } from '../services/reviews-copy-clipboard.service';
import { generateJWTToken } from 'src/app/core/helpers/jwt-token.utils';
import { SelectedLocationsStore, SelectedLocationsState } from '../../loc-comps/selected-locations.store';
import { ReviewsService } from '../reviews.service';
import { DownloadJobsService } from '../services/download-jobs.service';

@Component({
  selector: 'app-google-reviews-tab',
  templateUrl: './google-reviews-tab.component.html',
  styleUrls: ['./google-reviews-tab.component.scss'],
})
export class GoogleReviewsTabComponent implements OnInit, OnDestroy {
  SYSTEM_COLORS = SYSTEM_COLORS;
  @ViewChild('reviewTbl', { static: false }) reviewTbl: Table;

  datePosted: any[];
  keyword = '';
  sortOrder = '';
  /** Active client-side sort column applied to the current page of reviews. */
  private activeSortField: string = 'reviewCreatedAt';
  /** Active client-side sort direction: 1 ascending, -1 descending. */
  private activeSortOrder: number = -1;
  /** Review fields whose values are date objects and must be compared by timestamp. */
  private readonly dateSortFields: Set<string> = new Set(['reviewCreatedAt', 'reviewUpdatedAt', 'replyUpdatedAt']);
  filterData: IFilterData[] = [];
  chipsArray: string[] = [];
  showMultiReplyPopup = false;
  totalReviewsCount: number;
  reviewData: IReviewData[];
  selectedReviews: IReviewData[] = [];
  showOverview = true;
  cols: ICols[] = [];
  offset = 0;
  pageSize = 50;
  reviewsData: IReviewsMetrics;
  filters: IFitersObject = { DATE: ['Last 30 days'] };
  actionItems = [
    {
      label: `This page (0 items)`,
      command: () => {
        this.exportAllReviews(this.reviewTbl);
      },
      disabled: false,
      id: 'selectAll',
    },
    {
      label: 'All items (up to 10,000)',
      command: () => {
        this.exportFilteredReviews();
      },
    },
  ];
  public loading: boolean = true;
  public selectAllReviews: boolean = false;
  public search: string = '';
  public locationTagCounts: any;
  public displayReviewsCard: boolean = true;
  public dailyAvgRatings: any;
  public dailyTotalReviews: any;
  public startDate: string;
  public endDate: string;
  public dateFilterDisplayType: 'custom' | 'preset' = 'preset';
  public dateFilterLabel: string = PeriodText.LAST_30_DAYS;
  private readonly customDateRangeRegex = /^\d{4}-\d{2}-\d{2} - \d{4}-\d{2}-\d{2}$/;
  public isExporting: boolean = false;

  private enableSelectAllReviews: boolean = false;
  private ngUnsubscribe$: Subject<any> = new Subject();
  private keywordSearch$: Subject<string> = new Subject<string>();
  private selectedDateRange: string = 'DefaultDate';
  private userId: number;
  private dateRangeSet = new Set([
    'This month',
    'Last 30 days',
    'Last month',
    'Year to date',
    'Last 12 months',
  ]);
  private accountId: number;
  private loginId: number;
  private responsedReview: IReviewData | undefined;
  private reviewPageTokensByIndex: Record<number, string | null> = { 0: null };
  periodType: PeriodType;
  public locationFilter: string = '';

  constructor(
    private http: HttpClient,
    public dbService: DBService,
    public socketService: SocketService,
    private notifyService: NotifyService,
    private tableFilterService: TableFilterService,
    private dialogService: DialogService,
    public sessionService: SessionService,
    private exportDataToFile: ExportDataToFile,
    private reviewsCopyClipboardService: ReviewsCopyClipboardService,
    private selectedLocationsStore: SelectedLocationsStore,
    private reviewsService: ReviewsService,
    private downloadJobsService: DownloadJobsService
  ) {}

  private filtersQSFromStore(): string {
    const state = this.selectedLocationsStore.snapshot;
    if (!state) {
      return 'operand=AND';
    }

    const operand = `operand=${state.mode}`;
    if (state.storeCodes.length) {
      return `${operand}&storecode=${state.storeCodes.map(encodeURIComponent).join(',')}`;
    }
    const qs = this.buildQueryFromFiltersRecord(state.filters);
    return qs ? `${operand}&${qs}` : operand;
  }

  private buildQueryFromFiltersRecord(filters: Record<string, string[] | string>): string {
    const parts: string[] = [];
    Object.entries(filters).forEach(([apiKey, raw]) => {
      const qsKey = this.API_TO_QUERY[apiKey] ?? apiKey;
      const vals = Array.isArray(raw) ? raw : raw ? [String(raw)] : [];
      if (vals.length) parts.push(`${qsKey}=${encodeURIComponent(vals.join(','))}`);
    });
    return parts.join('&');
  }

  private readonly API_TO_QUERY: Record<string, string> = {
    storecode: 'storecode',
    business_names: 'businessname',
    city: 'city',
    states: 'state',
    countries: 'country',
    tag_list: 'tag',
    label_list: 'labels',
    category_list: 'categories',
  };

  /**
   * Initializes account and user context, sets default table/filter state, and wires reactive listeners.
   * It loads initial location-based data and subscribes to future location changes; search requests are debounced separately.
   * @returns None.
   */
  ngOnInit(): void {
    this.getCurrentAccount();
    this.getLoginId();
    this.initializeKeywordSearchDebounce();
    this.initUserFetchData()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(([account, user]: [IAccount, any]) => {
        this.accountId = account?._id;
        this.userId = user?.login?._id;
        const getQS = () => {
          const qs = this.filtersQSFromStore();
          return qs && qs.length ? qs : 'operand=AND';
        };

      // Initial load
      this.locationFilter = getQS();
      //this.getInsightsData(this.selectedRange, this.locationFilter);
      // Subsequent loads on any filter change
      this.selectedLocationsStore.state$
        .pipe(takeUntil(this.ngUnsubscribe$))
        .subscribe(() => {
          this.locationFilter = getQS();
          //this.getInsightsData(this.selectedRange, this.locationFilter);
          this.loadReviewsData();
        });
      });

    this.filterData.push(
      {
        type: FILTER_TYPES.MULTI,
        title: 'REVIEWS',
        options: ['Responded to', 'Not yet responded to'],
      },
      {
        type: FILTER_TYPES.MULTI,
        title: 'RATINGS',
        options: ['5', '4', '3', '2', '1'],
      },
      {
        type: FILTER_TYPES.DATE,
        title: 'DATE',
        options: ['This month', 'Last 30 days', 'Last month', 'Year to date', 'Last 12 months'],
      },
      {
        type: FILTER_TYPES.SINGLE,
        title: 'REVIEW TEXT',
        options: ['Review includes text', 'Review does not include text'],
      }
    );
    

    this.cols = [
      { field: 'storeCode', header: 'STORE CODE' },
      { field: 'businessName', header: 'LOCATION NAME' },
      { field: 'addressLines', header: 'STREET ADDRESS' },
      { field: 'city', header: 'CITY' },
      { field: 'state', header: 'STATE' },
      { field: 'postalCode', header: 'POSTAL CODE' },
      { field: 'tags', header: 'TAGS' },
      { field: 'reviewCreatedAt', header: 'REVIEW DATE' },
      { field: 'reviewerComment', header: 'REVIEW' },
      { field: 'reviewerName', header: 'REVIEWER NAME' },
      { field: 'starRating', header: 'RATING' },
      { field: 'replyUpdatedAt', header: 'RESPONSE DATE' },
      { field: 'replyComment', header: 'RESPONSE' },
    ];

    // this.broadcastService.subscribe('reviewGMBUpdated', (item) => {
    //   if (this.responsedReview) {
    //     // Removing the responded review from the list of all reviews.
    //     this.reviewData = this.reviewData.filter((r) => r._id !== this.responsedReview._id);
    //     // Updating the total reviews count.
    //     this.totalReviewsCount = this.totalReviewsCount - 1;
    //   }
    // });

    this.updateDateFilterDisplay();
  }

  private initUserFetchData(): Observable<[IAccount, any]> {
    this.getCurrentAccount();
    this.getCurrentUserDetails();

    return combineLatest([
      this.sessionService.getSelectedAccount$().pipe(rxFilter(Boolean), take(1)) as Observable<IAccount>,
      this.sessionService.getCurrentUser$().pipe(rxFilter(Boolean), take(1)),
    ]).pipe(takeUntil(this.ngUnsubscribe$)) as Observable<[IAccount, any]>;
  }

  private getCurrentUserDetails(): void {
    this.sessionService
      .getCurrentUser$()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((user) => {
        this.userId = user?.login?._id;
      });
  }

  /**
   * @description: Get the account details.
   */
  private getCurrentAccount(): void {
    this.sessionService
      .getSelectedAccount$()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((account: IAccount) => {
        this.accountId = account?._id;
      });
  }

  /**
   * @description: Get the current user details.
   * @returns: void
   * @arguments: void
   */
  private getLoginId(): void {
    this.sessionService
      .getCurrentUser$()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((user) => {
        this.loginId = user?.login?._id;
      });
  }

  /**
   * Configures debounced keyword search and triggers one data reload after 2s of typing inactivity.
   * Emitted values are deduplicated to avoid repeated requests for unchanged input.
   * @returns None.
   */
  private initializeKeywordSearchDebounce(): void {
    this.keywordSearch$
      .pipe(debounceTime(2000), distinctUntilChanged(), takeUntil(this.ngUnsubscribe$))
      .subscribe(() => {
        this.resetReviewsPagination();
        this.loadReviewsData();
      });
  }

  /**
   * Queues search input changes for debounced review loading instead of firing requests on every key press.
   * The latest `keyword` value is emitted, and pending debounce timers are reset by subsequent keystrokes.
   * @returns None.
   */
  userSearch(): void {
    this.keywordSearch$.next(this.keyword);
  }

  // dashboard
  toggleOverview() {
    this.showOverview = !this.showOverview;
  }

  hideOverview(toggle: boolean) {
    this.showOverview = !toggle;
  }

  // initial load
  /*loadReviewsData() {
    const request1$ = from(
      this.socketService.sendRequest('get-review-metrics', { includeAllTime: true, where: this.buildWhereClause() })
    );
    const request2$ = from(
      this.socketService.sendRequest('get-daily-review-metrics', {
        excludePast30Days: true,
        where: this.buildWhereClause(),
      })
    );

    zip(request1$, request2$)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(
        ([res1, res2]: [IReviewsMetricsResponse, any]) => {
          console.log("google reviews data");
          console.log(res1, res2);
          if (res1['collection'][0] || res2['collection']) {
            if (this.dateRangeSet.has(this.selectedDateRange)) {
              this.startDate = res1['collection'][0]?.dateStart;
              this.endDate = res1['collection'][0]?.dateEnd;
            }
            const dataSet1 = res1['collection'] && res1['collection']?.length && res1['collection'][0];
            this.totalReviewsCount = dataSet1['totalReviews'] || 0;
            this.dailyAvgRatings =
              res2['collection'] &&
              res2['collection'].length &&
              res2['collection'].map((el) => ({ date: el.date, value: +el.average_starrating }));
            this.dailyTotalReviews =
              res2['collection'] &&
              res2['collection'].length &&
              res2['collection'].map((el) => ({ date: el.date, value: +el.total_reviews }));
            this.reviewsData = { ...dataSet1 };
          } else {
            this.notifyService.error('There is no data for this account');
          }
        },
        (err: Error) => {
          this.notifyService.error('There is no data for this account');
        }
      );
  }*/
  /**
   * Builds the report query params (timeunit/periods plus search and review filters) from the active filter set.
   * All filter values are derived from a single source: the passed `filters` argument when provided,
   * otherwise the component's `this.filters`. Also sets `this.periodType` as a side effect based on the date filter.
   * Args:
   *   filters: Optional filter map (keys: 'DATE', 'RATINGS', 'REVIEW TEXT', 'REVIEWS').
   *     When undefined, falls back to `this.filters`.
   * Returns: An object with `{ timeunit, periods, search, ratings, reviewText, respondedTo }`.
   */
      AddFiltersToReviewData(filters = undefined) {
        const activeFilters = filters ?? this.filters;
        const search = this.keyword;
        const dateFilter = activeFilters?.['DATE']?.[0];
        const ratings = (activeFilters?.['RATINGS'] ?? []).join(',');
        const reviewText = activeFilters?.['REVIEW TEXT']?.[0];
        const respondedTo = activeFilters?.['REVIEWS']?.[0];
        if (!dateFilter) {
          this.periodType = PeriodType.DAY;
          return { timeunit: (PeriodType.DAY).toLowerCase(), periods: '30', search, ratings, reviewText, respondedTo };
        }
        //Custom date func, waiting for api implementation.
        const dateRangeRegex = /^\d{4}-\d{2}-\d{2} - \d{4}-\d{2}-\d{2}$/;
        if (dateRangeRegex.test(dateFilter)) {
          const [startDate, endDate] = dateFilter.split(' - ');
          const formattedStartDate = startDate.replace(/-/g, '');
          const formattedEndDate = endDate.replace(/-/g, '');
          this.periodType = PeriodType.CUSTOM_DATE;
          return { timeunit: PeriodType.CUSTOM_DATE.toLowerCase(), periods: `${formattedStartDate}_${formattedEndDate}`, search, ratings, reviewText, respondedTo };
        }
      /*if (dateRangeRegex.test(dateFilter)) {
        const [startStr, endStr] = dateFilter.split(' - ');
        const [sy, sm, sd] = startStr.split('-').map(Number);
        const [ey, em, ed] = endStr.split('-').map(Number);
        const startDate = new Date(sy, sm - 1, sd);
        const endDate = new Date(ey, em - 1, ed);
        const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    
        if (daysDiff > 60) {
          const monthsDiff = (endDate.getFullYear() - startDate.getFullYear()) * 12
            + (endDate.getMonth() - startDate.getMonth());
          this.periodType = PeriodType.MONTH;
          return { timeunit: (PeriodType.MONTH).toLowerCase(), periods: String(monthsDiff) };
        }
    
        this.periodType = PeriodType.DAY;
        return { timeunit: (PeriodType.DAY).toLowerCase(), periods: String(daysDiff) };
      }*/
    
      switch (dateFilter) {
        case PeriodText.THIS_MONTH: {
          const today = new Date();
          const daysPassedThisMonth = today.getDate();
          this.periodType = PeriodType.DAY;
          return { timeunit: (PeriodType.DAY).toLowerCase(), periods: String(daysPassedThisMonth-1), search, ratings, reviewText, respondedTo };
        }
    
        case PeriodText.LAST_30_DAYS: {
          this.periodType = PeriodType.DAY;
          return { timeunit: (PeriodType.DAY).toLowerCase(), periods: '30', search, ratings, reviewText, respondedTo };
        }
    
        case PeriodText.LAST_MONTH: {
          const today = new Date();
          const daysPassedThisMonth = today.getDate();
          this.periodType = PeriodType.DAY;
          return { timeunit: (PeriodType.DAY).toLowerCase(), periods: String(daysPassedThisMonth+31-1), search, ratings, reviewText, respondedTo };
        }
    
        case PeriodText.YEAR_TO_DATE: {
          const today = new Date();
          const startOfYear = new Date(today.getFullYear(), 0, 1);
          const daysPassedThisYear = Math.ceil((today.getTime() - startOfYear.getTime()) / (1000 * 60 * 60 * 24));
    
          if (daysPassedThisYear > 60) {
            const monthsPassed = today.getMonth(); 
            this.periodType = PeriodType.MONTH;
            return { timeunit: (PeriodType.MONTH).toLowerCase(), periods: String(monthsPassed), search, ratings, reviewText, respondedTo };
          }
    
          this.periodType = PeriodType.DAY;
          return { timeunit: (PeriodType.DAY).toLowerCase(), periods: String(daysPassedThisYear), search, ratings, reviewText, respondedTo };
        }
    
        case PeriodText.LAST_12_MONTHS: {
          this.periodType = PeriodType.MONTH;
          return { timeunit: (PeriodType.MONTH).toLowerCase(), periods: '12', search, ratings, reviewText, respondedTo };
        }
    
        case PeriodText.ALL_TIME: {
          this.periodType = PeriodType.YEAR;
          return { timeunit: (PeriodType.ALL_TIME).toLowerCase(), periods: '0', search, ratings, reviewText, respondedTo };
        }
    
        default:
          return { timeunit: (PeriodType.DAY).toLowerCase(), periods: '30', search, ratings, reviewText, respondedTo };
      }
    }

  /**
   * Loads review rows from the report API and normalizes each item to the table model.
   * Handles string or object payloads, maps snake_case API fields to the existing `IReviewData` shape,
   * and updates table data/counts used by pagination, export actions, and token-based next page retrieval.
   * Args: none.
   * Returns: `void`.
   */
  loadReviewsData(): void {
      this.loading = true;
      const {timeunit,periods,search,ratings,reviewText,respondedTo} = this.AddFiltersToReviewData(this.filters);
      const currentPageIndex = Math.floor((this.offset || 0) / this.pageSize);
      const nextPageToken = this.reviewPageTokensByIndex[currentPageIndex] ?? undefined;
      forkJoin({
        reviews:this.getExportedReport(
          this.accountId,
          this.userId,
          'reviews/all/page',
          timeunit,
          periods,
          search,
          this.locationFilter,
          nextPageToken,
          ratings,
          reviewText,
          respondedTo
        ),
      }).pipe(takeUntil(this.ngUnsubscribe$))
        .subscribe(({ reviews}) => {
         // console.log("reviews",reviews); //DO NOT REMOVE THIS LOG
          const buildReviewDate = (value: string | null | undefined): any | null => {
            if (!value) {
              return null;
            }
            const parsedDate = new Date(value.replace(' ', 'T'));
            if (Number.isNaN(parsedDate.getTime())) {
              return null;
            }
            const year = parsedDate.getFullYear();
            const month = parsedDate.getMonth() + 1;
            const day = parsedDate.getDate();
            const hour = parsedDate.getHours();
            const minute = parsedDate.getMinutes();
            return {
              _year_: year,
              _month_: month,
              _day_: day,
              year,
              month,
              day,
              hour,
              minute,
            };
          };

          const payload = (() => {
            try {
              if (typeof reviews !== 'string') {
                return reviews;
              }
              // API may return non-JSON tokens like NaN; normalize those to valid JSON values.
              const normalizedReviews = reviews.replace(/:\s*NaN(?=\s*[,}\]])/g, ': null');
              return JSON.parse(normalizedReviews);
            } catch (parseError) {
              console.error('Error parsing reviews payload:', parseError);
              return {};
            }
          })();
          const parsedReviews = Array.isArray(payload?.reviews) ? payload.reviews : [];
          const apiNextPageToken = payload?.next_page_token ? String(payload.next_page_token) : null;
          const apiTotalRecordsRetrieved = Number(payload?.Total_records_retrieved);

          const mappedReviews: IReviewData[] = parsedReviews.map((review: any, index: number) => {
            const reviewCreatedAt = buildReviewDate(review.reviewcreatedat ?? review.date);
            const reviewUpdatedAt = buildReviewDate(review.reviewupdatedat) ?? reviewCreatedAt;
            const replyUpdatedAt = buildReviewDate(review.responseupdatedat);
            const reviewerComment = review.review ?? null;
            const replyComment = review.response ?? null;
            const tags = String(review.tags || '')
              .split(',')
              .map((tag) => tag.trim())
              .filter((tag) => tag.length > 0);

            const mappedReview: IReviewData = {
              _id: index + 1,
              reviewId: review.review_id ?? '',
              reviewIdent: review.review_id ?? '',
              account: '',
              account_id: this.accountId,
              attrs: null,
              businessName: review.business_name ?? '',
              addressLines: review.address ? [review.address] : [],
              city: review.city ?? '',
              state: review.state ?? '',
              postalCode: review.postal_code ?? '',
              date_id: 0,
              location_id: 0,
              replyComment,
              replyUpdatedAt,
              reviewCreatedAt,
              reviewUpdatedAt,
              reviewerComment,
              reviewerIsAnonymous: null,
              reviewerName: review.reviewer_name ?? '',
              reviewerProfileUrl: review.url ?? '',
              starRating: Number(review.rating ?? 0),
              storeCode: review.storecode ?? '',
              directory: review.vendorident ?? '',
              vendorIdent: review.vendorident ?? '',
              tags,
            };

            if (reviewerComment && reviewCreatedAt) {
              mappedReview.reviewTooltip = `<span style="font-style: italic; padding-bottom: 15px">"${reviewerComment}"</span>
                           <p style="text-align: left; margin-top: 12px"><b>Posted by</b> ${mappedReview.reviewerName || 'Unknown'}</p>
                           <p style="text-align: left; margin: 0">on <b>${reviewCreatedAt.day}/${reviewCreatedAt.month}/${reviewCreatedAt.year} ${reviewCreatedAt.hour}:${reviewCreatedAt.minute}</b></p>`;
            }

            if (replyComment && replyUpdatedAt) {
              mappedReview.replyTooltip = `<span style="font-style: italic; padding-bottom: 15px">"${replyComment}"</span>
                           <p style="text-align: left; margin-top: 12px"><b>Posted on ${replyUpdatedAt.day}/${replyUpdatedAt.month}/${replyUpdatedAt.year} ${replyUpdatedAt.hour}:${replyUpdatedAt.minute}</b></p>`;
            }

            return mappedReview;
          });

          this.reviewData = mappedReviews
          this.applySortToCurrentPage();

          if (apiNextPageToken) {
            this.reviewPageTokensByIndex[currentPageIndex + 1] = apiNextPageToken;
          } else {
            delete this.reviewPageTokensByIndex[currentPageIndex + 1];
          }

          const fallbackTotal = this.offset + this.reviewData.length + (apiNextPageToken ? 1 : 0);
          this.totalReviewsCount = Number.isFinite(apiTotalRecordsRetrieved)
            ? Math.max(apiTotalRecordsRetrieved, fallbackTotal)
            : fallbackTotal;
          this.actionItems.forEach((item) => {
            if (item.id === 'selectAll') {
              item.label = `This page (${this.reviewData.length} items)`;
            }
          });
          this.loading = false;
        },
        (err: Error) => {
          this.loading = false;
          console.error('Error loading reviews data:', err);
        }
      );
      
    }

      /**
   * Builds the report query string while avoiding duplicate `storecode` params.
   * Existing location filters remain authoritative, and the search term is only applied as `storecode` when the filters do not already define one.
   * @param timeunit The report aggregation unit expected by the insights API.
   * @param periods The selected period value sent to the insights API.
   * @param filters Serialized global filter query string, if any.
   * @param search Optional search-box value that maps to the `storecode` query param.
   * @param nextPageToken Optional continuation token used to request the next review page.
   * @returns The normalized report query string.
   */
  private buildReportQueryString(
    timeunit: string,
    periods: any,
    filters?: string,
    search?: string,
    nextPageToken?: string,
    ratings?: string,
    reviewText?: string,
    respondedTo?: string
  ): string {
    const queryParams = new URLSearchParams({
      timeunit,
      periods: String(periods),
      limit: String(50),
    });

    if (filters && filters.length) {
      const filterParams = new URLSearchParams(filters);
      filterParams.forEach((value, key) => queryParams.append(key, value));
    }

    if(search && search.length > 0) {
      queryParams.set('search_term', search);
    }

    if (ratings && ratings.length > 0) {
      queryParams.set('rating', ratings);
    }

    if (nextPageToken) {
      queryParams.set('next_page_token', nextPageToken);
    }

    if (reviewText && reviewText.length > 0) {
      queryParams.set('reviewtext', reviewText == "Review includes text" ? "true" : "false");
    }

    if(respondedTo && respondedTo.length > 0) {
      queryParams.set('reply', respondedTo == "Responded to" ? "true" : "false");
    }

    return queryParams.toString();
  }

  /**
   * Builds the export query string from active local and global filters.
   * It mirrors review-table filters (date, ratings, response status, review text, keyword) and merges selected location filters.
   * @param timeunit API time unit derived from selected date filter.
   * @param periods API period value derived from selected date filter.
   * @param filters Serialized global location filters from SelectedLocationsStore.
   * @param search Optional keyword search value.
   * @param ratings Optional comma-separated rating values.
   * @param reviewText Optional review text filter label.
   * @param respondedTo Optional response-status filter label.
   * @returns Serialized query string for export endpoint calls.
   */
  private buildExportQueryString(
    timeunit: string,
    periods: any,
    filters?: string,
    search?: string,
    ratings?: string,
    reviewText?: string,
    respondedTo?: string
  ): string {
    const queryParams = new URLSearchParams({
      timeunit,
      periods: String(periods),
    });

    if (filters && filters.length) {
      const filterParams = new URLSearchParams(filters);
      filterParams.forEach((value, key) => queryParams.append(key, value));
    }

    if (search && search.length > 0) {
      queryParams.set('search_term', search);
    }

    if (ratings && ratings.length > 0) {
      queryParams.set('rating', ratings);
    }

    if (reviewText && reviewText.length > 0) {
      queryParams.set('reviewtext', reviewText === 'Review includes text' ? 'true' : 'false');
    }

    if (respondedTo && respondedTo.length > 0) {
      queryParams.set('reply', respondedTo === 'Responded to' ? 'true' : 'false');
    }

    return queryParams.toString();
  }

  /**
   * Exports reviews from the Google Reviews tab using active global and local filters.
   * It reuses current account/user context, applies date/rating/response/text/search filters, and downloads the exported CSV result.
   * While the request is in flight, the export button is disabled to prevent duplicate submissions.
   * @returns None.
   */
  public exportReviewsWithFilters(): void {
    if (!this.accountId || !this.userId) {
      this.notifyService.error('Unable to export reviews until account and user are loaded.');
      return;
    }

    const { timeunit, periods, search, ratings, reviewText, respondedTo } = this.AddFiltersToReviewData(this.filters);
    const globalLocationFilters = this.locationFilter?.length ? this.locationFilter : this.filtersQSFromStore();
    const queryString = this.buildExportQueryString(
      timeunit,
      periods,
      globalLocationFilters,
      search,
      ratings,
      reviewText,
      respondedTo
    );

    this.isExporting = true;
    this.downloadJobsService.notifyExportStarted();
    this.reviewsService
      .exportReviewsNewEndpoint(this.userId, this.accountId, 'reviews/all/export', queryString)
      .pipe(take(1), takeUntil(this.ngUnsubscribe$), finalize(() => (this.isExporting = false)))
      .subscribe(
        (response: string) => {
          //this.reviewsService.genCSVReport(response, 'csv', 'google-reviews-export');
          this.downloadJobsService.notifyJobsUpdated();
        },
        () => {
          this.notifyService.error('Unable to export reviews. Please try again.');
          this.downloadJobsService.notifyJobsUpdated();
        }
      );
  }

        /**
   * Fetches the metrics export report for the current account and user.
   * The request always includes the selected time range, preserves serialized location filters, and only adds the search-based `storecode` when the filters do not already supply one.
   * @param accountId The current account identifier included in the JWT payload.
   * @param userId The current user identifier included in the JWT payload.
   * @param directoryName The report directory path segment under the insights API.
   * @param timeunit The report aggregation unit expected by the insights API.
   * @param periods The selected period value sent to the insights API.
   * @param search Optional search-box value that maps to the `storecode` query param when no filter store code is present.
   * @param filters Optional serialized location filter query string appended to the request.
   * @param nextPageToken Optional continuation token used to request the next review page.
   * @returns An observable that emits the CSV report response as plain text.
   */
  public getExportedReport(
    accountId: any,
    userId: any,
    directoryName: string,
    timeunit: string,
    periods: any,
    search?: string,
    filters?: string,
    nextPageToken?: string,
    ratings?: string,
    reviewText?: string,
    respondedTo?: string
  ): Observable<any> { 
    const payloadObj = {
      iss: 'https://platform.renderseo.com', 
      sub: userId, 
      account_id: accountId,
      iat: new Date().getTime(),
      exp: new Date().getTime() + 3 * 6000,
    };
    const jwtToken = generateJWTToken(payloadObj, '2c4c4d06f0b35b921039d9df2725205d981ac00bf196eb71b02ce7571432e30a');
    const options = {
      headers: new HttpHeaders({
        'content-type': 'application/json',
        Authorization: `${jwtToken}`,
      }),
      responseType: 'text' as any,
    };

    const queryString = this.buildReportQueryString(timeunit, periods, filters, search, nextPageToken,ratings,reviewText,respondedTo);
    //console.log("sending request");
    const url = `https://io.renderseo.com/${directoryName}?${queryString}`;
    //console.log(url,options);
    return this.http.get(url, options);
  }

  private buildWhereClause(): Array<string> {
    const filterClauses: string[][] = [];
    const tempFilterArr: string[] = [];
    const isFiltersApplied = this.chipsArray.length > 0;

    const pushQueriesToMainArray = () => {
      let filterClause: Array<string> = ['-or'];
      filterClause = filterClause.concat(tempFilterArr);
      filterClauses.push(filterClause);
      tempFilterArr.length = 0;
    };

    // build query if filtering is used
    if (isFiltersApplied) {
      for (const [key, value] of Object.entries(this.filters)) {
        if (value.length > 0 && value[0] != undefined) {
          switch (key) {
            case 'TAGS':
              value.forEach((tag: string) => tempFilterArr.push(`tags @> '{"${tag}"}'`));
              pushQueriesToMainArray();
              break;
            case 'REVIEWS':
              value.forEach((element: string) => {
                element == 'Responded to'
                  ? tempFilterArr.push(`NOT replyComment IS NULL`)
                  : tempFilterArr.push(`replyComment IS NULL`);
              });
              pushQueriesToMainArray();
              break;
            case 'RATINGS':
              value.forEach((element: string) => {
                switch (element) {
                  case '5':
                    tempFilterArr.push(`starRating = 5`);
                    break;
                  case '4':
                    tempFilterArr.push(`starRating = 4`);
                    break;
                  case '3':
                    tempFilterArr.push(`starRating = 3`);
                    break;
                  case '2':
                    tempFilterArr.push(`starRating = 2`);
                    break;
                  case '1':
                    tempFilterArr.push(`starRating = 1`);
                    break;
                }
              });
              pushQueriesToMainArray();
              break;
            case 'DATE':
              value.forEach((element: string) => {
                this.selectedDateRange = element;
                switch (element) {
                  case 'This month':
                    tempFilterArr.push(
                      `extract(month from r.date) = extract(month from current_date) and extract(year from r.date) = extract(year from current_date)`
                    );
                    break;
                  case 'Last 30 days':
                    tempFilterArr.push(`r.date > (now() - interval '30 day')`);
                    break;
                  case 'Last month':
                    tempFilterArr.push(
                      `extract(month from r.date) = extract(month from current_date) - 1 and extract(year from r.date) = extract(year from current_date)`
                    );
                    break;
                  case 'Year to date':
                    tempFilterArr.push(`extract(year from r.date) = extract(year from current_date)`);
                    break;
                  case 'Last 12 months':
                    tempFilterArr.push(`r.date > (now() - interval '12 month')`);
                    break;
                  case 'DefaultDate':
                    this.startDate = new Date().toISOString().slice(0, 10);
                    this.endDate = new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().slice(0, 10);
                    tempFilterArr.push(`r.date > (now() - interval '30 day')`);
                    break;
                  default:
                    const dateArray: string[] = element.split(' - ');
                    this.startDate = dateArray[0];
                    this.endDate = dateArray[1];
                    tempFilterArr.push(`reviewCreatedAt >= '${dateArray[0]}' AND reviewCreatedAt <= '${dateArray[1]}'`);
                    break;
                }
              });
              pushQueriesToMainArray();
              break;
            case 'REVIEW TEXT':
              value.forEach((element: string) => {
                switch (element) {
                  case 'Review includes text':
                    tempFilterArr.push(`reviewercomment IS NOT NULL`);
                    break;
                  case 'Review does not include text':
                    tempFilterArr.push(`reviewercomment IS NULL`);
                    break;
                }
              });
              pushQueriesToMainArray();
              break;
          }
        }
      }
    } else {
      this.selectedDateRange = 'DefaultDate';
      this.startDate = new Date().toISOString().slice(0, 10);
      this.endDate = new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().slice(0, 10);
      // Adding default lst 30 days option if no date filter is selected.
      tempFilterArr.push(`r.date > (now() - interval '30 day')`);
      pushQueriesToMainArray();
    }

    // build query if searching is used
    if (this.keyword) {
      tempFilterArr.push(`storeCode::text ~* '\\y${this.keyword}'`);
      tempFilterArr.push(`businessName::text ~* '\\y${this.keyword}'`);
      tempFilterArr.push(`tags::text ~* '\\y${this.keyword}'`);
      tempFilterArr.push(`addressLines::text ~* '\\y${this.keyword}'`);
      tempFilterArr.push(`city::text ~* '\\y${this.keyword}'`);
      tempFilterArr.push(`state::text ~* '\\y${this.keyword}'`);
      tempFilterArr.push(`postalCode::text ~* '\\y${this.keyword}'`);
      tempFilterArr.push(`reviewerComment::text ~* '\\y${this.keyword}'`);
      tempFilterArr.push(`replyComment::text ~* '\\y${this.keyword}'`);
      pushQueriesToMainArray();
    }

    // join all queries
    let whereClauseArray: Array<any> = ['-and', "r._status IN ('A', 'UA', 'UP')", "vendorIdent='gmb'"];
    whereClauseArray = whereClauseArray.concat(filterClauses);
    return whereClauseArray;
  }

  // private loadReviews() {
  //   this.loading = true;

  //   if (this.loading) {
  //     this.selectAllReviews = false;
  //     if (!this.selectAllReviews) {
  //       this.selectedReviews = [];
  //     }
  //   }

  //   this.dbService.ReviewGMB.loadObjects({
  //     columns: ['state'],
  //     offset: this.offset,
  //     limit: this.pageSize,
  //     order: this.sortOrder,
  //     where: this.buildWhereClause(),
  //   })
  //     .then(
  //       (repl) => {
  //         this.reviewData = repl.collection.map((item) => {
  //           return {
  //             ...item,
  //             tags: item?.tags && item?.tags.length && item.tags.join(', ').replace(/_/g, ' ').split(','),
  //           };
  //         });

  //         this.totalReviewsCount = repl.totalEntries;

  //         this.actionItems.forEach((item) => {
  //           if (item.id === 'selectAll') {
  //             item.label = `This page (${this.reviewData.length} items)`;
  //           }
  //         });

  //         for (const obj of this.reviewData) {
  //           this.datePosted = obj.reviewCreatedAt;
  //           if (obj.reviewerComment != null) {
  //             obj.reviewTooltip = `<span style="font-style: italic; padding-bottom: 15px">"${obj.reviewerComment}"</span>
  //                          <p style="text-align: left; margin-top: 12px"><b>Posted by</b> ${obj.reviewerName} on <b>${obj.reviewCreatedAt.month}/${obj.reviewCreatedAt.day}/${obj.reviewCreatedAt.year} ${obj.reviewCreatedAt.hour}:${obj.reviewCreatedAt.minute}</b></p>`;
  //           }
  //           if (obj.replyComment != null) {
  //             obj.replyTooltip = `<span style="font-style: italic; padding-bottom: 15px">"${obj.replyComment}"</span>
  //                          <p style="text-align: left; margin-top: 12px"><b>Posted on ${obj.replyUpdatedAt.month}/${obj.replyUpdatedAt.day}/${obj.replyUpdatedAt.year} ${obj.replyUpdatedAt.hour}:${obj.replyUpdatedAt.minute}</b></p>`;
  //           }
  //         }
  //       },
  //       (err: Error) => {
  //         this.reviewData = [];
  //         console.warn('Error loading ReviewGMB: %j', err);
  //       }
  //     )
  //     .finally(() => {
  //       this.loading = false;
  //     });
  // }

  openEditResponse(review: IReviewData) {
    this.dialogService
      .open(AddEditResponseComponent, {
        data: {
          review: review,
          accountId: this.accountId,
          loginId: this.userId,
        },
        closable: false,
        width: '70%',
        height: '70%',
      })
      .onClose.pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((res: { publishObject, confirmed: boolean }) => {
        if (!res.confirmed) {
          return;
        };
        const data = res.publishObject.data;
        this.reviewData = this.reviewData.map((r) => {
          if (r._id === data._id) {
            return {
              ...r,
              replyComment: data.replyComment,
            };
          }
          return r;
        });
      });
  }

  openDeleteResponse(review: IReviewData) {
    this.dialogService.open(DeletePopupComponent, {
      data: {
        ...review,
        actionType: 'Response',
        accountId: this.accountId,
        loginId: this.userId,
      },
      closable: false,
      width: '40%',
    });
  }

  deleteReview(review: IReviewData) {
    this.dialogService
      .open(DeletePopupComponent, {
        data: { ...review, actionType: 'Review',accountId: this.accountId, loginId: this.userId},
        closable: false,
        width: '40%',
      })
      .onClose.subscribe((res) => {
        if (res) {
          this.notifyService.success('Success: Review deleted successfully.');
          this.loadReviewsData();
        } else {
          this.notifyService.error('Error: Error in deleting the Review. Please try again!');
        }
      });
  }

  /**
   * Opens the multi-reply dialog after attaching required auth identifiers to each selected review.
   * Ensures downstream publish flows can read `loginId` and `account_id` directly from selected review data.
   * Args: none.
   * Returns: `void`.
   */
  addMultiReply(): void {
    this.selectedReviews = this.selectedReviews.map((review) => ({
      ...review,
    }));
    this.showMultiReplyPopup = true;
  }

  cancelMultiReply() {
    this.showMultiReplyPopup = false;
  }

  /**
   * Handles close events from the multi-reply dialog.
   * When confirmation is successful, it merges the returned multi-reply payload into the current review list.
   * @param dialogResult Close toggle or `{ publishObject, confirmed }` payload emitted by multi-reply flow.
   * @returns None.
   */
  getInfoFromDialog(dialogResult: boolean | { publishObject: IPublish<IReviewMulti[]>; confirmed: boolean }): void {
    if (typeof dialogResult !== 'boolean' && dialogResult?.confirmed) {
      const updatedReviews = dialogResult.publishObject?.data ?? [];
      const updatedById = new Map(updatedReviews.map((review) => [review._id, review.replyComment]));
      this.reviewData = this.reviewData.map((review) =>
        updatedById.has(review._id) ? { ...review, replyComment: updatedById.get(review._id) } : review
      );
    }

    this.showMultiReplyPopup = false; // close the dialog
    this.selectedReviews = [];
    this.selectAllReviews = false;
  }

  exportReviews(table: Table, all?: boolean) {
    table.exportCSV(all ? '' : { selectionOnly: true });
  }

  public exportAllReviews(table: Table): void {
    this.enableSelectAllReviews = !this.enableSelectAllReviews;
    if (this.enableSelectAllReviews) {
      this.selectedReviews = this.reviewData;
      table.exportCSV();
    }
  }

  public lazyLoadLocations(event: LazyLoadEvent): void {
    if (event.sortField == 'directory') {
      return;
    }

    const nextSortOrder = event.sortField ? `${event.sortField}${event.sortOrder < 0 ? ' DESC' : ''}` : this.sortOrder;
    const isSortChanged = nextSortOrder !== this.sortOrder;
    if (isSortChanged) {
      this.sortOrder = nextSortOrder;
      this.sortCurrentPageReviews(event.sortField, event.sortOrder);
      return;
    }

    const nextOffset = event.first || 0;
    const nextPageIndex = Math.floor(nextOffset / this.pageSize);
    if (nextPageIndex > 0 && !(nextPageIndex in this.reviewPageTokensByIndex)) {
      this.notifyService.error('Unable to load that page yet. Please move page by page.');
      return;
    }

    this.offset = nextOffset;
    this.loadReviewsData();
  }

  /**
   * Records the chosen sort column/direction and reorders only the reviews currently on the page.
   * No API request is made; pagination and totals are left untouched.
   * @param sortField Review field selected for sorting (e.g. `starRating`).
   * @param sortOrder Sort direction from the table: 1 ascending, -1 descending.
   * @returns None.
   */
  private sortCurrentPageReviews(sortField: string, sortOrder: number): void {
    if (!sortField) {
      return;
    }
    this.activeSortField = sortField;
    this.activeSortOrder = sortOrder;
    this.applySortToCurrentPage();
  }

  /**
   * Reorders the in-memory current-page reviews by the active sort column and direction.
   * No-ops when there is no active column or no loaded reviews.
   * @returns None.
   */
  private applySortToCurrentPage(): void {
    if (!this.activeSortField || !this.reviewData?.length) {
      return;
    }
    const direction = this.activeSortOrder < 0 ? -1 : 1;
    this.reviewData = [...this.reviewData].sort(
      (first, second) =>
        this.compareReviewValues(
          this.getReviewSortValue(first, this.activeSortField),
          this.getReviewSortValue(second, this.activeSortField)
        ) * direction
    );
  }

  /**
   * Resolves the comparable value for a review field, converting date objects to timestamps.
   * @param review Review row to read the sort value from.
   * @param field Review field name being sorted.
   * @returns A timestamp for date fields, otherwise the raw field value, or null when absent.
   */
  private getReviewSortValue(review: IReviewData, field: string): number | string | null {
    const value = review?.[field as keyof IReviewData];
    if (this.dateSortFields.has(field)) {
      return this.reviewDateToTimestamp(value);
    }
    return value as number | string | null;
  }

  /**
   * Converts a mapped review date object into a millisecond timestamp for comparison.
   * @param reviewDate Date object produced during review mapping (`{ year, month, day, hour, minute }`).
   * @returns Timestamp in milliseconds, or null when the date is missing/invalid.
   */
  private reviewDateToTimestamp(reviewDate: any): number | null {
    if (!reviewDate || !reviewDate.year) {
      return null;
    }
    const { year, month = 1, day = 1, hour = 0, minute = 0 } = reviewDate;
    return new Date(year, month - 1, day, hour, minute).getTime();
  }

  /**
   * Compares two review sort values with null-safe, numeric-aware ordering.
   * Nulls sort first; numbers compare numerically; everything else compares as locale strings.
   * @param first First value to compare.
   * @param second Second value to compare.
   * @returns Negative when `first` precedes `second`, positive when it follows, 0 when equal.
   */
  private compareReviewValues(
    first: number | string | null,
    second: number | string | null
  ): number {
    if (first == null && second == null) {
      return 0;
    }
    if (first == null) {
      return -1;
    }
    if (second == null) {
      return 1;
    }
    if (typeof first === 'number' && typeof second === 'number') {
      return first - second;
    }
    return String(first).localeCompare(String(second), undefined, { numeric: true, sensitivity: 'base' });
  }

  onChipsRefresh(filters: IFitersObject) {
    this.chipsArray = [].concat.apply([], Object.values(filters)); // merge all checkbox values into one array
    this.chipsArray = this.chipsArray.filter((e) => e); // remove all possible undefined
    if (!filters.hasOwnProperty('DATE')) {
      filters = { ...filters, DATE: ['DefaultDate'] };
    } else if (filters.hasOwnProperty('DATE') && !filters['DATE'].length) {
      filters = { ...filters, DATE: ['DefaultDate'] };
    }

    this.filters = filters;
    this.updateDateFilterDisplay(filters?.['DATE']?.[0]);
    this.resetReviewsPagination();
    this.loadReviewsData();
  }

  /**
   * Returns whether the DATE filter value is a custom ISO range (`YYYY-MM-DD - YYYY-MM-DD`).
   * @param dateFilter Raw DATE filter value from table-filter.
   * @returns True when the value matches the custom date range format.
   */
  private isCustomDateRange(dateFilter: string | undefined): boolean {
    return !!dateFilter && this.customDateRangeRegex.test(dateFilter);
  }

  /**
   * Updates the reviews date header to match the active DATE filter.
   * Custom ranges show `Reviews from` with explicit dates; presets show the filter label only.
   * @param dateFilter Optional DATE filter value; falls back to `this.filters.DATE[0]` when omitted.
   * @returns None.
   */
  private updateDateFilterDisplay(dateFilter?: string): void {
    const activeDateFilter = dateFilter ?? this.filters?.['DATE']?.[0];

    if (this.isCustomDateRange(activeDateFilter)) {
      const [start, end] = activeDateFilter.split(' - ');
      this.dateFilterDisplayType = 'custom';
      this.startDate = start;
      this.endDate = end;
      this.dateFilterLabel = '';
      return;
    }

    this.dateFilterDisplayType = 'preset';
    this.startDate = null;
    this.endDate = null;
    this.dateFilterLabel = this.resolvePresetDateFilterLabel(activeDateFilter);
  }

  /**
   * Maps internal/default DATE filter values to user-facing preset labels.
   * @param dateFilter Raw DATE filter value from table-filter.
   * @returns Preset label shown in the reviews date header.
   */
  private resolvePresetDateFilterLabel(dateFilter: string | undefined): string {
    if (!dateFilter || dateFilter === 'DefaultDate') {
      return PeriodText.LAST_30_DAYS;
    }

    return dateFilter;
  }

  /**
   * Resets token-based pagination state to the first review page.
   * Clears tracked next-page tokens and resets the table offset so the next load starts from page zero.
   * Args: none.
   * Returns: `void`.
   */
  private resetReviewsPagination(): void {
    this.offset = 0;
    this.reviewPageTokensByIndex = { 0: null };
  }

  onRowSelect(event) {
    this.selectAllReviews = this.selectedReviews.length === this.reviewData.length;
  }

  public exportFilteredReviews(): void {
    const params = {
      order: this.sortOrder,
      where: this.buildWhereClause(),
    };

    this.exportDataToFile.getReviews(params);
  }

  public handleSelectAllReviews(event): void {
    this.selectedReviews = event?.checked ? this.reviewData : [];
  }

  public toggleReviewsCardView() {
    this.displayReviewsCard = !this.displayReviewsCard;
  }

  public copyReviewToClipboard(review: IReviewData): void {
    const reviewsObjectCopy = this.reviewsCopyClipboardService.mapReviewDataCopyObject(review);
    const reviewsTextCopy = this.reviewsCopyClipboardService.mapReviewsDataCopyString(reviewsObjectCopy);
    const textArea = document.createElement('textarea');
    textArea.value = reviewsTextCopy;
    navigator.clipboard.writeText(textArea.value);
    this.notifyService.success('Review copied to clipboard');
  }

  ngOnDestroy(): void {
    this.tableFilterService.clearFilters(); // clear table filters
    this.keywordSearch$.complete();
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
